import { Auth } from './amplifyCompat';
import { buildCloudflareUrl } from '../config/cloudflare';
import { captureException, logger, startSpan } from '../monitoring/sentry';
import { isTransientNetworkError, withRetry } from '../utils/network-retry';

const getCognitoAdminApiUrl = () => buildCloudflareUrl('/api/cognito-admin');

// Helper to get auth headers
const getAuthHeaders = async () => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  } catch {
    throw new Error('User not authenticated');
  }
};

// Generic function to call the duplicate Cloudflare Worker route.
const callCognitoAdminApi = async (action, params, { signal, retry = true } = {}) => {
  return startSpan(
    {
      op: 'http.client',
      name: `POST Cognito Admin ${action}`,
    },
    async (span) => {
      span.setAttribute('cognito.action', action);

      try {
        const headers = await getAuthHeaders();
        const apiUrl = getCognitoAdminApiUrl();

        const execute = () => fetch(apiUrl, {
            method: 'POST',
            headers,
            signal,
            body: JSON.stringify({
              action,
              ...params
            })
          });
        const response = retry
          ? await withRetry(execute, { label: `Cognito Admin ${action}`, signal })
          : await execute();

        const raw = await response.text();
        let data = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch {
          data = { message: raw };
        }

        if (!response.ok) {
          const workerError = data?.errors?.[0]?.message;
          const error = new Error(data.message || workerError || `HTTP ${response.status}`);
          error.code = data.error || data?.errors?.[0]?.errorType;
          throw error;
        }

        return data;
      } catch (error) {
        if (isTransientNetworkError(error)) {
          logger.warn(`Skipping Sentry capture for transient Cognito Admin API network error (${action})`, {
            action,
            message: error?.message,
          });
          throw error;
        }

        captureException(error, {
          tags: {
            module: 'cognitoAdminService',
            action,
          },
        });
        logger.error(`Cognito Admin API Error (${action})`, { action });
        throw error;
      }
    }
  );
};

// Specific API functions
export const cognitoAdminService = {
  // Get user details
  adminGetUser: async (username, options = {}) => {
    const result = await callCognitoAdminApi('adminGetUser', { username }, options);
    return result.user;
  },

  // Create new user
  adminCreateUser: async (params, options = {}) => {
    const {
      username,
      userAttributes,
      temporaryPassword,
      messageAction = 'SUPPRESS'
    } = params;

    const result = await callCognitoAdminApi('adminCreateUser', {
      username,
      userAttributes,
      temporaryPassword,
      messageAction
    }, { ...options, retry: false });

    return result.user;
  },

  // Set user password
  adminSetUserPassword: async (params, options = {}) => {
    const {
      username,
      password,
      permanent = true
    } = params;

    await callCognitoAdminApi('adminSetUserPassword', {
      username,
      password,
      permanent
    }, options);

    return { success: true };
  },

  // Update user attributes
  adminUpdateUserAttributes: async (params, options = {}) => {
    const {
      username,
      userAttributes
    } = params;

    await callCognitoAdminApi('adminUpdateUserAttributes', {
      username,
      userAttributes
    }, options);

    return { success: true };
  }
};

export default cognitoAdminService;
